import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from '@remix-run/react';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { db, chatId } from '~/lib/persistence/useChatHistory';
import { getMessagesById, updateNetlifyUrl } from '~/lib/persistence/db';
import { initiateOAuth, resetOAuthState } from '~/lib/utils/netlify-helpers';
import type { UseNetlifyAuthReturn, NetlifyUser, ChatDeployment } from '~/lib/types/netlify';
import { useInterval } from './useInterval';
import { setHasExistingDeployment, setError } from '~/lib/stores/netlify';

const validateNetlifyToken = async (token: string): Promise<boolean> => {
  console.log('[validateNetlifyToken] Validating token...');

  try {
    const response = await fetch('https://api.netlify.com/api/v1/user', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.ok;
  } catch (error) {
    return false;
  }
};

export function useNetlifyAuth(
  netlifyConfig: {
    NEXT_PUBLIC_NETLIFY_CLIENT_ID: string;
    NEXT_PUBLIC_NETLIFY_CLIENT_SECRET: string;
    NEXT_PUBLIC_NETLIFY_APP_URL: string;
  },
  cleanupLogs?: () => void,
): UseNetlifyAuthReturn {
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [accessToken, setAccessToken] = useState<string | null>();
  const [user, setUser] = useState<NetlifyUser | null>(null);
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(false);
  const [tokenCheckCount, setTokenCheckCount] = useState(0);

  // Validate token function
  const validateToken = useCallback(async (): Promise<boolean> => {
    if (!accessToken) {
      return false;
    }

    setIsValidatingToken(true);

    const isValid = await validateNetlifyToken(accessToken);
    setIsValidatingToken(false);

    if (!isValid) {
      handleDisconnect();
    }

    return isValid;
  }, [accessToken]);

  // Fetch user details
  const fetchUserDetails = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    try {
      const response = await fetch('https://api.netlify.com/api/v1/user', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        handleDisconnect();
        return;
      }

      const userData = await response.json();
      setUser(userData as NetlifyUser);
    } catch (err) {
      console.error('Error fetching user details:', err);
      handleDisconnect();
    }
  }, [accessToken]);

  // Handle disconnect
  const handleDisconnect = useCallback(() => {
    console.log('[useNetlifyAuth] Disconnecting Netlify account');
    selectionStore.setKey('netlifyAccessToken', '');
    selectionStore.setKey('netlifySiteId', '');
    setAccessToken(null);
    setUser(null);

    // Reset deployment state
    setHasExistingDeployment(false);
    setError('');

    // Clean up logs when disconnecting Netlify account
    if (cleanupLogs) {
      cleanupLogs();
    }
  }, [cleanupLogs]);

  // Initiate OAuth flow
  const initiateOAuthFlow = useCallback(() => {
    setTokenCheckCount(0);
    setIsCheckingToken(true);
    initiateOAuth(netlifyConfig, setIsCheckingToken, setTokenCheckCount);
  }, [netlifyConfig]);

  // Reset auth state
  const resetAuthState = useCallback(() => {
    resetOAuthState(setIsCheckingToken, setTokenCheckCount);
  }, []);

  // Interval to check for token availability during OAuth flow
  useInterval(
    () => {
      const storedToken = selectionStore.get().netlifyAccessToken;

      if (storedToken) {
        setAccessToken(storedToken);
        setIsCheckingToken(false);
        setTokenCheckCount(0);
        return;
      }

      setTokenCheckCount((prev) => {
        const newCount = prev + 1;

        if (newCount >= 30) {
          setIsCheckingToken(false);
          return 0;
        }

        return newCount;
      });
    },
    isCheckingToken ? 1000 : null,
  );

  // Handle OAuth callback
  useEffect(() => {
    // Handle OAuth callback
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const savedState = localStorage.getItem('netlifyState');

    if (state === savedState) {
      fetch('/api/netlify-oauth/callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
        .then((res) => res.json())
        .then((data: unknown) => {
          const tokenData = data as { access_token?: string };

          if (tokenData.access_token) {
            setAccessToken(tokenData.access_token);
            selectionStore.setKey('netlifyAccessToken', tokenData.access_token);
            localStorage.removeItem('netlifyState');
            navigate(location.pathname, { replace: true });
          }
        });
    }
  }, [location, navigate]);

  // Check for existing token in chat or storage
  useEffect(() => {
    const getAccessToken = async () => {
      // Early return if no db or chatId
      if (!db || !chatId.get()) {
        return await handleStoredTokenValidation();
      }

      const chat = await getMessagesById(db, chatId.get()!);
      const chatData = 'netlifyCreds' in chat ? (chat as ChatDeployment) : null;
      const chatToken = chatData?.netlifyCreds?.netlifyToken;
      const storedToken = selectionStore.get().netlifyAccessToken;

      // If no chat token, validate stored token
      if (!chatToken) {
        return await handleStoredTokenValidation();
      }

      // Validate chat token
      const isChatTokenValid = await validateNetlifyToken(chatToken);

      if (isChatTokenValid) {
        setAccessToken(chatToken);
        selectionStore.setKey('netlifyAccessToken', chatToken);
        return true;
      }

      // Chat token invalid, try stored token
      const isStoredTokenValid = storedToken && (await validateNetlifyToken(storedToken));

      if (isStoredTokenValid) {
        setAccessToken(storedToken);
        await updateNetlifyCreds(db, chatId.get()!, chatData, storedToken);
        return true;
      }

      // Both tokens invalid, clear credentials
      await updateNetlifyCreds(db, chatId.get()!, chatData, '');
      handleDisconnect();
      return false;
    };

    const handleStoredTokenValidation = async () => {
      const storedToken = selectionStore.get().netlifyAccessToken;
      const isValid = await validateNetlifyToken(storedToken);

      if (isValid) {
        setAccessToken(storedToken);
        return true;
      }

      handleDisconnect();
      return false;
    };

    const updateNetlifyCreds = async (
      db: IDBDatabase,
      chatId: string,
      chatData: ChatDeployment | null,
      token: string,
    ) => {
      await updateNetlifyUrl(db, chatId, {
        netlifyToken: token,
        netlifyUrl: chatData?.netlifyCreds?.netlifyUrl || '',
        netlifySiteId: chatData?.netlifyCreds?.netlifySiteId || '',
        inspectDeployment: chatData?.netlifyCreds?.inspectDeployment || '',
      });
    };

    getAccessToken();
  }, []);

  // Effect to fetch user details when accessToken changes
  useEffect(() => {
    if (accessToken) {
      fetchUserDetails();
    }
  }, [accessToken, fetchUserDetails]);

  return {
    accessToken,
    user,
    isValidatingToken,
    isCheckingToken,
    tokenCheckCount,
    validateToken,
    fetchUserDetails,
    handleDisconnect,
    initiateOAuthFlow,
    resetAuthState,
  };
}
